import { DefaultBrandKitValue } from '@src/components/sidepanel/CompositionSettings/types';

import type {
  AnyElement,
  ControlConfiguration,
  MediaElement,
  ShapeElement,
  SpeakerControlConfiguration,
  TextElement,
} from '@src/components/sidepanel/CompositionSettings/types';

export type CompositionControlGroupDato = {
  fieldName: string;
  controls: ControlConfiguration[];
};

/** An `ai_prompt` record with `prompt_type: "Graphics"` (PLAT-10912). */
export type GraphicsPromptOption = { id: string; title: string };

const generateControlId = (element: ControlConfiguration) => {
  return `control_${
    element.type
  }_${element?.property}_${element?.label}_${element?.elementPaths
    .flat()
    .join('_')
    .replaceAll(' ', '_')}`;
};

export class CompositionControlsGenerator {
  /**
   * Gets element names from control element paths
   * @param control - The control configuration with elementPaths
   * @param sourceContext - The source context (Creatomate JSON) to traverse
   * @returns Comma-separated string of element names
   */
  public getElementNamesByControlPaths(
    sourceContext: any,
    elementPath: number[],
  ): string {
    const path = elementPath as number[];
    const element = path.reduce((acc, curr) => {
      return acc?.elements?.[curr];
    }, sourceContext);
    return element?.name || '';
  }

  /**
   * Generates controls from Creatomate JSON structure
   * @param creatomateJSON - The Creatomate composition JSON object
   * @returns Array of control configurations
   */
  public generateControlsFromCreatomate(
    creatomateJSON: any,
  ): CompositionControlGroupDato[] {
    // Skip the root canvas and start with its elements
    if (!creatomateJSON.elements || !Array.isArray(creatomateJSON.elements)) {
      return [];
    }
    // @ts-ignore no property for speaker control
    const speakerControl: SpeakerControlConfiguration = {
      label: 'Speaker',
      elementPaths: [],
      controlBindings: {
        speaker_name: 'name',
        speaker_title: 'title',
      },
      type: 'speaker',
      enabled: false,
    };
    // Start traversing from the root elements (canvas children)
    // The first element (composition) will have path [0], its children [0,0], [0,1], etc.
    const allGenerated = [
      { fieldName: 'Speaker', controls: [speakerControl] },
      ...this.traverseElements(creatomateJSON.elements),
    ];

    const selectedMergeFieldProperty: {
      groupIndex: number;
      controlIndex: number;
    }[] = [];
    allGenerated.forEach((controlGroup, groupIndex) => {
      controlGroup.controls.forEach((control, controlIndex) => {
        if (control.property === 'font_family') {
          selectedMergeFieldProperty.push({ groupIndex, controlIndex });
        }
      });
    });

    const mergedControls = this.mergeControls(
      allGenerated,
      selectedMergeFieldProperty,
    );

    return mergedControls;
  }

  /**
   * Recursively traverses Creatomate JSON structure and generates controls for all elements
   * @param elements - Array of elements from Creatomate JSON
   * @param currentPath - Current path in the element tree (array of indices)
   * @returns Array of all controls with their paths
   */
  private traverseElements(
    elements: AnyElement[],
    currentPath: number[] = [],
  ): CompositionControlGroupDato[] {
    const allControls: CompositionControlGroupDato[] = [];
    elements.forEach((element, index) => {
      const elementPath = [...currentPath, index];

      // Generate controls for this element
      const controls = this.generateControl(element, elementPath);
      allControls.push({ fieldName: element.name || '', controls: controls });

      // If element has nested elements (like composition), traverse them recursively
      if ('elements' in element && Array.isArray(element.elements)) {
        const nestedControls = this.traverseElements(
          element.elements,
          elementPath,
        );
        allControls.push(...nestedControls);
      }
    });

    return allControls;
  }

  private generateControl(
    element: AnyElement,
    elementPath: number[],
  ): ControlConfiguration[] {
    switch (element.type) {
      case 'text':
        return this.getControlsForText(element as TextElement, elementPath);
      case 'shape':
        return this.getControlsForShape(element as ShapeElement, elementPath);
      case 'image':
        return this.getControlsForImage(element as MediaElement, elementPath);
      case 'composition':
        return this.getControlsForComposition(
          element as TextElement,
          elementPath,
        );
      default:
        return [];
    }
  }

  private getControlsForText(
    element: TextElement,
    elementPath: number[],
  ): ControlConfiguration[] {
    const elementControls: ControlConfiguration[] = [];
    elementControls.push({
      label: `Text ${element?.name}`,
      elementPaths: [elementPath],
      property: 'text',
      type: 'text',
      enabled: true,
      ...(element?.text ? { default: { value: element.text } } : {}),
    });
    elementControls.push({
      default: {
        brandKit: DefaultBrandKitValue.PRIMARY_COLOR,
      },
      label: `Text color ${element?.name}`,
      elementPaths: [elementPath],
      property: 'fill_color',
      type: 'color',
      enabled: 'fill_color' in element,
    });
    elementControls.push({
      default: {
        brandKit: DefaultBrandKitValue.SECONDARY_COLOR,
      },
      elementPaths: [elementPath],
      label: `Background color ${element?.name}`,
      property: 'background_color',
      type: 'color',
      enabled: 'background_color' in element,
    });
    elementControls.push({
      default: {
        brandKit: DefaultBrandKitValue.DEFAULT_FONT,
      },
      elementPaths: [elementPath],
      label: `Font ${element?.name}`,
      property: 'font_family',
      type: 'font',
      enabled: 'font_family' in element,
    });
    elementControls.push({
      elementPaths: [elementPath],
      label: `Font Weight ${element?.name}`,
      property: 'font_weight',
      type: 'number',
      enabled: 'font_weight' in element,
    });
    elementControls.push({
      elementPaths: [elementPath],
      label: `Opacity ${element?.name}`,
      property: 'opacity',
      type: 'number',
      enabled: 'opacity' in element,
      ...(element?.opacity ? { default: { value: element.opacity } } : {}),
    });

    return elementControls.map(control => ({
      ...control,
      id: generateControlId(control),
    }));
  }

  private getControlsForComposition(
    element: TextElement,
    elementPath: number[],
  ): ControlConfiguration[] {
    const elementControls: ControlConfiguration[] = [];
    elementControls.push({
      elementPaths: [elementPath],
      label: `Background Color ${element?.name}`,
      property: 'fill_color',
      type: 'color',
      enabled: 'fill_color' in element,
    });
    return elementControls.map(control => ({
      ...control,
      id: generateControlId(control),
    }));
  }

  private getControlsForShape(
    element: ShapeElement,
    elementPath: number[],
  ): ControlConfiguration[] {
    const elementControls: ControlConfiguration[] = [];
    elementControls.push({
      default: {
        brandKit: DefaultBrandKitValue.SECONDARY_COLOR,
      },
      elementPaths: [elementPath],
      label: `Background color ${element?.name}`,
      property: 'fill_color',
      type: 'color',
      enabled: 'fill_color' in element,
    });
    elementControls.push({
      elementPaths: [elementPath],
      label: `Border radius ${element?.name}`,
      property: 'border_radius',
      type: 'number',
      enabled: 'border_radius' in element,
      ...(element?.border_radius
        ? { default: { value: element.border_radius } }
        : {}),
    });
    elementControls.push({
      elementPaths: [elementPath],
      label: `Opacity ${element?.name}`,
      property: 'opacity',
      type: 'number',
      enabled: 'opacity' in element,
      ...(element?.opacity ? { default: { value: element.opacity } } : {}),
    });
    return elementControls.map(control => ({
      ...control,
      id: generateControlId(control),
    }));
  }

  private getControlsForImage(element: MediaElement, elementPath: number[]) {
    const elementControls: ControlConfiguration[] = [];
    const isHeadshot = /headshot/i.test(element?.name || '');
    elementControls.push({
      elementPaths: [elementPath],
      label: `${isHeadshot ? 'Headshot' : 'Image'} ${element?.name}`,
      property: 'source',
      type: isHeadshot ? 'headshot' : 'image',
      enabled: 'source' in element,
    });
    return elementControls.map(control => ({
      ...control,
      id: generateControlId(control),
    }));
  }

  /**
   * Flattens the authored control groups into the array stored on the
   * composition, refreshing every `aiText` control's `promptTitle` from its
   * `promptId` (PLAT-10912 DS2) — the title is decoration, so it self-heals
   * after a rename in DatoCMS instead of drifting forever.
   *
   * Returns the errors that must block the save (DS4): an `aiText` control's
   * `id` is required, because generated options are keyed by it, and its prompt
   * must resolve to a Graphics prompt.
   *
   * `graphicsPrompts` is `null` when the prompt list could not be loaded. That
   * is NOT the same as an empty list: with no list to check against, a control
   * that already carries a `promptId` is left exactly as authored — validating
   * it against nothing would block the save on every AI control over what may
   * be a transient network failure, and re-deriving the title would erase it.
   */
  public prepareControlsForSave(
    controlGroups: CompositionControlGroupDato[],
    graphicsPrompts: GraphicsPromptOption[] | null,
  ): { controls: ControlConfiguration[]; errors: string[] } {
    const promptTitlesById = graphicsPrompts
      ? new Map(graphicsPrompts.map(prompt => [prompt.id, prompt.title]))
      : null;
    const errors: string[] = [];

    const controls = controlGroups
      .flatMap(controlGroup => controlGroup.controls)
      .map(control => {
        if (control.type !== 'aiText') {
          return control;
        }

        if (!control.id?.trim()) {
          errors.push(`AI text control "${control.label}" needs an id.`);
        }

        // A missing promptId is an authoring error either way — there is
        // nothing to generate with, and no list is needed to see that.
        if (!control.promptId?.trim()) {
          errors.push(
            `AI text control "${control.label}" needs a Graphics prompt.`,
          );
          return control;
        }

        if (!promptTitlesById) return control;

        const promptTitle = promptTitlesById.get(control.promptId);
        if (!promptTitle) {
          errors.push(
            `AI text control "${control.label}" needs a Graphics prompt.`,
          );
          return control;
        }

        return { ...control, promptTitle };
      });

    return { controls, errors };
  }

  public mergeControls(
    controlGroups: CompositionControlGroupDato[],
    selectedMergeFieldProperty: { groupIndex: number; controlIndex: number }[],
  ): CompositionControlGroupDato[] {
    const mergedControlGroup: CompositionControlGroupDato = {
      fieldName: '',
      controls: [],
    };
    let templateControl: ControlConfiguration | null = null;

    // Iterate through control groups and filter out controls with matching property
    controlGroups = controlGroups.map((controlGroup, groupIndex) => {
      controlGroup.controls = controlGroup.controls.filter(
        (control, controlIndex) => {
          if (
            selectedMergeFieldProperty.some(
              item =>
                item.groupIndex === groupIndex &&
                item.controlIndex === controlIndex,
            )
          ) {
            // Take the first found control as the template (element 0)
            if (!templateControl) {
              templateControl = {
                ...control,
                id: generateControlId(control),
                elementPaths: [], // Reset elementPaths, we'll collect them
                enabled: true,
              };
            }

            // Add control group fieldname to the merged fieldname
            mergedControlGroup.fieldName = mergedControlGroup.fieldName
              ? [mergedControlGroup.fieldName, controlGroup.fieldName].join(
                  ', ',
                )
              : controlGroup.fieldName;

            // Add the path to elementPaths
            templateControl.elementPaths.push(...control.elementPaths);
            if (!templateControl.label.includes(controlGroup.fieldName)) {
              templateControl.label = [
                templateControl.label,
                controlGroup.fieldName,
              ].join(', ');
            }

            return false; // Filter out this control from the original group
          }
          return true;
        },
      );
      return controlGroup;
    });

    // If we found any controls to merge, add the merged control group
    if (templateControl && mergedControlGroup.fieldName) {
      mergedControlGroup.controls = [templateControl];
      const existingControlGroup = controlGroups.find(
        item => item.fieldName === mergedControlGroup.fieldName,
      );
      if (existingControlGroup) {
        existingControlGroup.controls.push(...mergedControlGroup.controls);
      } else {
        controlGroups.push(mergedControlGroup);
      }
    }

    return controlGroups.filter(
      controlGroup => controlGroup.controls.length > 0,
    );
  }

  /**
   * Splits controls with multiple element paths into separate control groups
   * This is the opposite of mergeControlsByProperty
   * @param controlGroups - The control groups containing controls to split
   * @returns Array of control groups with split controls
   */
  public splitControlsByElementPaths(
    sourceContext: any,
    controlGroups: CompositionControlGroupDato[],
    splittingGroupIndex: number,
    splittingControlIndex: number,
  ): CompositionControlGroupDato[] {
    const splitControlGroups: CompositionControlGroupDato[] = [];

    controlGroups.forEach((controlGroup, index) => {
      // Keep control groups that are not being split as is
      if (index !== splittingGroupIndex) {
        splitControlGroups.push(controlGroup);
        return;
      }

      // For the group being split, get the control to split
      const controlToSplit = controlGroup.controls[splittingControlIndex];

      // Keep all other controls in the original group (excluding the one being split)
      const remainingControls = controlGroup.controls.filter(
        (_, i) => i !== splittingControlIndex,
      );

      if (remainingControls.length > 0) {
        splitControlGroups.push({
          fieldName: controlGroup.fieldName,
          controls: remainingControls,
        });
      }

      // Split the selected control by its elementPaths
      controlToSplit.elementPaths.forEach(elementPath => {
        // Create a new control for this element path
        const fieldName = this.getElementNamesByControlPaths(
          sourceContext,
          elementPath,
        );

        let newControlLabel =
          controlToSplit.label.replace(controlGroup.fieldName, '') + fieldName;

        const splitControl: ControlConfiguration = {
          ...controlToSplit,
          label: newControlLabel,
          elementPaths: [elementPath],
          id: generateControlId({
            ...controlToSplit,
            elementPaths: [elementPath],
          }),
        };

        // Get the fieldName for this split control

        // Find or create a group for this fieldName
        const existingGroup = splitControlGroups.find(
          group => group.fieldName === fieldName,
        );

        if (existingGroup) {
          existingGroup.controls.push(splitControl);
        } else {
          splitControlGroups.push({
            fieldName,
            controls: [splitControl],
          });
        }
      });
    });

    return splitControlGroups;
  }
}
